import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Params } from '@angular/router';
import { AutoUnsubscribe, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, skip, switchMap, tap } from 'rxjs/operators';
import { MenuInterface, routes } from 'src/app/_core/components/layout-nav/routes';
import { LayoutComponent } from 'src/app/_core/components/layout/layout.component';
import {
    UserDelegationPickerDialogComponent,
    UserDelegationPickerDialogData
} from 'src/app/_user/dialogs/user-delegation-picker-dialog/user-delegation-picker-dialog.component';
import { LayoutRoute, LayoutService } from 'src/app/_core/services/layout.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { Dispatch } from 'src/app/_core/tools/decorators/dispatch.decorator';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    EditPasswordDialogComponent,
    EditPasswordDialogData
} from 'src/app/_shared/dialogs/edit-password-dialog/edit-password-dialog.component';
import {
    SharedNavigationManagerResetAction
} from 'src/app/_shared/stores/shared-navigation-manager/shared-navigation-manager.actions';
import { SelectMenuAction, SetActivatedMenusListAction } from 'src/app/_user/store/menu/menu.action';
import { MenuState } from 'src/app/_user/store/menu/menu.state';
import { LogoutAction, } from 'src/app/_user/store/user/user.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { MenuDto, UsersDto } from 'src/graphql/generated-types';

@Component({
    selector: 'app-layout-nav',
    templateUrl: './layout-nav.component.html',
    styleUrls: ['layout-nav.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutNavComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

    user$: Observable<UsersDto> = inject(Store).select(({user}) => user.connectedUser);

    ceName$: Observable<string> = inject(Store).select(({user}) => UserState.currentCeName(user));

    menusList$: Observable<MenuDto[]> = inject(Store).select(({menu}) => menu?.menusList || []);

    displayedMenusList: MenuDto[] = [];

    childrenOfMenus: { [key: string]: MenuDto[] };

    selectedMenu: MenuDto = null;

    previousMenusList: MenuDto[] = [];

    displayedSubMenusList: MenuDto[] = [];

    activedMenusId: number[] = [];

    @ViewChild('subNavigation', {static: true}) subNavigationRef: TemplatePortal;

    @ViewChildren('navigation') navigationRef: QueryList<ElementRef>;

    menusList: MenuDto[] = [];

    subMenuOpenSubject = new Subject<boolean>();

    private overlayRef: OverlayRef;

    @AutoUnsubscribe()
    private overlayBackdropClick: Subscription = Subscription.EMPTY;

    private readonly menusToRemove = ['Module_PortailPrevisualisation'];

    private readonly menusToRedirect: { [classMenu: string]: () => void } = {
        Module_PortailStatistiquesPiwik: () => this.navigateToStatistics()
    };

    private readonly menusToCheckBeforeAccess: { [classMenu: string]: (menu: MenuDto) => { canAccess: boolean, cannotAccessReason?: string } } = {
        Module_PortailMenus: () => this.canAccessToMenusArticles(),
        Module_PortailPortlets: (menu) => this.canAccessToPortailPortlets(menu)
    };

    // Barre de recherche des sous-menus
    searchbarControl = new FormControl('');

    // liste de référence sur laquelle on va baser le tri de displayedSubMenuList pour la barre de recherche
    searchbarSubMenuDataSource: MenuDto[] = [];

    constructor(protected cdr: ChangeDetectorRef,
                private readonly overlay: Overlay,
                private readonly vcr: ViewContainerRef,
                private readonly dialogService: KalDialogService,
                private readonly layoutService: LayoutService,
                @Optional() private layoutComponent: LayoutComponent) {
        super(cdr);
    }

    get isBaseTest(): boolean {
        return this.snapshot(UserState.modulesList)?.includes('BASETEST');
    }

    get currentSubMenu(): MenuDto {
        const size = this.previousMenusList.length;
        return size > 0 ? this.previousMenusList[size - 1] : null;
    }

    /**
     * Permet de savoir si le sous menu est affiché pour cacher l'icone dans le layout.
     */
    get isSubMenuOpen$(): Observable<boolean> {
        return this.subMenuOpenSubject;
    }

    openDelegationChoiceDialog(): void {
        if(!this.snapshot(UserState.needChoice)) {
            return;
        }

        const dialogConfig = new KalDialogConfig<UserDelegationPickerDialogData>({
            title: 'Changer la délégation',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                currentDelegationData: {
                    delegationId: this.snapshot(UserState.currentDelegation).Id,
                    sellPointId: this.snapshot(UserState.currentSellPoint).ID,
                    deskId: this.snapshot(UserState.currentCaisse).Id,
                    stockId: this.snapshot(UserState.currentStock).Id
                }
            }
        });

        this.watch$ = this.dialogService.open(UserDelegationPickerDialogComponent, dialogConfig).afterClosed.pipe(
            filter(dialogResult => !!dialogResult?.accepted),
            tap(() => {
                this.closeMenu();
            })
        );
    }

    private get positionStrategy(): PositionStrategy {
        return this.overlay.position()
            .flexibleConnectedTo(this.layoutService.navigationRefElement)
            .withPositions([
                {originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top'}
            ]);
    }

    getMenuFromId(id: number): MenuDto {
        return this.menusList.find(e => e.Id === id);
    }

    getRouteFromMenu(menu: MenuDto): MenuInterface {
        return routes.find(route => ('' + route.code === '' + this.getClassMenu(menu)) && (!route.additionalConditionFn || route.additionalConditionFn(menu)));
    }

    @Dispatch(new LogoutAction())
    logOut() {
    }

    selectMenu(menu: MenuDto): void {
        this.searchbarControl.reset('', { emitEvent: false });

        const activatedMenusList = this.store.selectSnapshot(MenuState.activatedMenusList);
        if (this.hasChildren(menu)) {

            if (activatedMenusList.length > 1 && activatedMenusList.some(e => e.Id === menu.Id) && this.selectedMenu !== menu) {
                this.previousMenusList = [...activatedMenusList.slice(1, activatedMenusList.length - 2)];
                this.selectSubMenu(activatedMenusList[activatedMenusList.length - 2]);
            } else {
                this.previousMenusList = [];
                this.displayedSubMenusList = this.filterMenus(menu);
                this.searchbarSubMenuDataSource = this.displayedSubMenusList;
            }

            this.selectedMenu = menu;

            if (!this.overlayRef || !this.overlayRef.hasAttached()) {
                this.getOverlayRef().attach(this.subNavigationRef);
                this.subMenuOpenSubject.next(true);
            }

        } else {
            this.navigate(menu);
            this.getOverlayRef().detach();
            this.subMenuOpenSubject.next(false);
        }
    }

    navigate(menu: MenuDto): void {
        const {route, code} = this.getRouteFromMenu(menu) || {};
        const checkAccessResult = this.menusToCheckBeforeAccess[code];

        const accessObject = checkAccessResult ? checkAccessResult(menu) : null;

        if (accessObject && !accessObject.canAccess) {
            this.openCannotAccessToMenuDialog(accessObject.cannotAccessReason);
        } else if (route) {
            const value = menu?.MenuPage?.ListeParametres?.MenuPageParamDTO?.[0]?.Valeur || null;
            this.closeMenu();

            this.watch$ = this.dispatch$(new SharedNavigationManagerResetAction()).pipe(
                switchMap(() => this.dispatch$(new SelectMenuAction())),
                switchMap(() => {
                    const extra = typeof route[1] === 'string' ? [route[1], {}] : [route[1].primaryRoute, route[1].queryParams];
                    return this.layoutService.navigate(route[0], {
                        primaryRoute: extra[0] as keyof LayoutRoute,
                        queryParams: {
                            idMenu: menu.Id,
                            value,
                            ...extra[1] as Params
                        }
                    });
                }),
                switchMap(() => this.dispatch$(new SelectMenuAction(menu))),
            );

        } else if (this.menusToRedirect[this.getClassMenu(menu)]) {
            this.menusToRedirect[this.getClassMenu(menu)]();
        }
    }

    selectSubMenu(menu: MenuDto): void {
        this.searchbarControl.reset('', { emitEvent: false });

        // Specifique pour (CEV2_AIRBUSSLAT)
        if (menu.Code === 'AIRBUS_FORMULAIRE') {
            const url = this.snapshot(UserState.pathsCE).GestionUrlBase + 'ESN/AirbusFormulaire/?numSession=' + this.snapshot(UserState.userContext).NumSession;
            window.open(url, '_blank');
            this.watch$ = this.layoutService.navigateToHome();
        }

        if (this.hasChildren(menu)) {
            this.previousMenusList.push(menu);
            this.displayedSubMenusList = this.childrenOfMenus[menu.Id];
            this.searchbarSubMenuDataSource = this.displayedSubMenusList;
        } else {
            this.navigate(menu);
        }
    }

    previousMenu(menu: MenuDto): void {
        if (!menu) {
            this.selectMenu(this.selectedMenu);
            return;
        }

        const menuIndex = this.previousMenusList.findIndex(e => e.Id === menu.Id);
        this.previousMenusList.splice(menuIndex);
        this.selectSubMenu(menu);
        this.cdr.markForCheck(); // utilisé pour la gestion du fil d'Ariane dans les pages
    }

    hasChildren(menu: MenuDto): boolean {
        return this.childrenOfMenus[menu.Id] ? this.childrenOfMenus[menu.Id].length > 0 : false;
    }

    closeMenu(): void {
        this.selectedMenu = null;
        this.displayedSubMenusList = [];
        this.previousMenusList = [];
        this.searchbarSubMenuDataSource = [];
        this.searchbarControl.reset('', { emitEvent: false });
        this.detachOverlay();

        this.subMenuOpenSubject.next(false);
    }

    isActive(menu: MenuDto): boolean {
        return this.activedMenusId.indexOf(menu.Id) > -1;
    }

    openPortalDialog(): void {
        this.watch$ = this.layoutService.openPortalDialog$().pipe(
            tap(() => this.closeMenu())
        );
    }

    navigateToHome(): void {
        this.watch$ = this.layoutService.navigateToHome();
    }

    openEditPassword(): void {
        const config = new KalDialogConfig<EditPasswordDialogData>({
            title: 'Modifier votre mot de passe',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });

        this.dialogService.open(EditPasswordDialogComponent, config);
    }

    private getClassMenu(menu: MenuDto): string {
        return menu?.MenuPage?.Classe || null;
    }

    private filterMenus(menu: MenuDto): MenuDto[] {
        const menus = this.childrenOfMenus[menu.Id];
        return menus.filter(m => this.menusToRemove.indexOf(this.getClassMenu(m)) === -1);
    }

    private navigateToStatistics(): void {
        const {url, login, password} = this.snapshot(UserState.statisticCredentials) || {
            url: null,
            login: null,
            password: null
        };
        if ([url, login, password].every(e => !!e)) {
            window.open(`https://${url}/index.php?module=Login&action=logme&login=${login}&password=${password}`);
        } else {
            const config = new KalDialogConfig<ConfirmDialogData>({
                data: {
                    body: 'Votre compte de statistiques n\'est pas paramétré, veuillez vous rapprocher du service client',
                    submitLabel: 'Fermer'
                },
                hasBackdrop: true,
                backdropClass: 'cdk-overlay-dark-backdrop',
            });
            this.dialogService.open(ConfirmDialogComponent, config);
        }
    }

    private getOverlayRef(): OverlayRef {
        if (!this.overlayRef) {

            const panelClass: string[] = ['gup-C-subMenu__panel'];

            if (this.isBaseTest) {
                panelClass.push('gup-C-subMenu--isBaseTest');
            }
            const config: OverlayConfig = {
                disposeOnNavigation: true,
                minWidth: '250px',
                width: 'auto',
                height: '100vh',
                panelClass,
                backdropClass: ['gup-C-subMenu__backdrop', '-layout-nav'],
                hasBackdrop: true,
                positionStrategy: this.positionStrategy
            };

            this.overlayRef = this.overlay.create(config);

            // remove previous subscription
            if (this.overlayBackdropClick) {
                this.overlayBackdropClick.unsubscribe();
            }
            // create new subscription for backdrop click
            this.overlayBackdropClick = this.overlayRef.backdropClick()
                .pipe(
                    this.untilDestroyed,
                    tap(() => this.closeMenu())
                ).subscribe();
        }
        return this.overlayRef;
    }

    private detachOverlay(): void {
        if (this.overlayRef) {
            this.overlayRef.detach();
        }
    }

    private getParentMenu(menu: MenuDto): void {
        if (menu) {
            this.activedMenusId.push(menu.Id);
            if (menu.ParentMenu && menu.ParentMenu.Id) {
                this.getParentMenu(this.menusList.find(e => e.Id === menu.ParentMenu.Id));
            }
        }
    }

    private canAccessToMenusArticles(): { canAccess: boolean } {
        const hasMenusArticleModule = this.snapshot(UserState.modulesList).indexOf('MENUPORTAILGLOB') > -1;
        const isOnGlobalDelegation = this.snapshot(UserState.currentDelegation).CEIdcGlob;

        return {canAccess: !isOnGlobalDelegation ? true : hasMenusArticleModule};
    }

    private canAccessToPortailPortlets(menu: MenuDto): { canAccess: boolean, cannotAccessReason?: string } {
        const isPublic = (menu.MenuPage.ListeParametres.MenuPageParamDTO.find(e => e.Libelle === 'CONTEXTE')?.Valeur || 'PUBLIC') === 'PUBLIC';
        const isOnGlobalDelegation = this.snapshot(UserState.currentDelegation).CEIdcGlob;

        if (isPublic) {
            const hasMultiDeleg = this.snapshot(UserState.hasMultiDelegationsModule);
            return {
                canAccess: (isOnGlobalDelegation && hasMultiDeleg) || (!isOnGlobalDelegation && !hasMultiDeleg)
            };
        } else {
            const hasUpGestionModule = this.snapshot(UserState.modulesList).indexOf('UPGESTION') > -1;
            if (hasUpGestionModule) {
                return {
                    canAccess: false,
                    cannotAccessReason: 'L\'accès à ce menu n\'est pas autorisé pour les clients Essentiel.'
                };
            }
            const hasPorltetsModule = this.snapshot(UserState.modulesList).indexOf('ACCUEILPORTAILGLOB') > -1;
            return {canAccess: (isOnGlobalDelegation && hasPorltetsModule) || (!isOnGlobalDelegation && !hasPorltetsModule)};
        }
    }

    private openCannotAccessToMenuDialog(reason: string): void {
        const config = new KalDialogConfig<ConfirmDialogData>({
            title: 'Impossible d\'accéder à la page',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                body: reason || 'Vous n\'êtes pas autorisé à accéder à cette page.',
                submitLabel: 'Ok'
            }
        });

        this.dialogService.open(ConfirmDialogComponent, config);
    }

    ngOnInit(): void {
        this.subMenuOpenSubject.next(false);

        this.watch$ = this.layoutService.routeChanges$.pipe(
            skip(1),
            tap(() => {
                this.activedMenusId = [];
                const selectedRouteId = this.store.selectSnapshot(MenuState.selectedMenu)?.Id || null;
                const menu = this.menusList.find(e => '' + e.Id === '' + selectedRouteId);

                if (menu) {
                    this.getParentMenu(menu);
                    this.dispatch$(new SetActivatedMenusListAction(this.activedMenusId.reverse().map(e => this.getMenuFromId(e))));
                }

                this.cdr.markForCheck();
            })
        );

        this.watch$ = this.menusList$.pipe(
            tap(
                menusList => {
                    this.menusList = menusList || [];
                    this.displayedMenusList = this.menusList.filter(e => e.ParentMenu === null);
                    this.cdr.markForCheck();

                    const menus: { [key: string]: MenuDto[] } = {};
                    this.menusList.forEach(menu => {
                        const menuWithParent = this.menusList.filter(e => e.ParentMenu && '' + e.ParentMenu.Id === '' + menu.Id);

                        if (menuWithParent.length > 0) {
                            menus[menu.Id] = menuWithParent;
                        }
                    });

                    this.childrenOfMenus = menus;
                })
        );

        this.watch$ = this.searchbarControl.valueChanges.pipe(
            tap(searchbarValue => this.displayedSubMenusList = this.searchbarSubMenuDataSource.filter(m => m.Libelle.toLowerCase().includes(searchbarValue.toLowerCase())))
        );
    }

    ngAfterViewInit(): void {
        // wait for navigation html element to appear
        this.layoutService.navigationRefElement = this.navigationRef.first;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        // quand on quitte ce composant, il faut fermer le menu
        // pour le logout par example
        this.closeMenu();
        this.subMenuOpenSubject.complete();
    }
}
